/* eslint-disable no-console */
/* eslint-disable no-restricted-properties */
import pino from 'pino';
import type { Logger as PinoLogger } from 'pino';
import * as Sentry from '@sentry/react';
import type { Logger } from './types';

let loggerInstance: Logger | undefined = undefined;

export interface LoggerConfig {
  environment: string;
}

export function createLogger(config: LoggerConfig): Logger {
  // TODO: remove after testing and proper config setup via environment variables
  console.info('Creating logger', config);

  const pinoLogger: PinoLogger = pino({
    browser: {
      asObject: true,
      // Use console methods for output in browser
      write: {
        trace: (o: unknown) => {
          logToSentry('trace', o);
          if (config.environment === 'development') {
            console.trace(o);
          }
        },
        debug: (o: unknown) => {
          logToSentry('debug', o);
          if (config.environment === 'development') {
            console.debug(o);
          }
        },
        info: (o: unknown) => {
          logToSentry('info', o);
          console.info(o);
        },
        warn: (o: unknown) => {
          logToSentry('warning', o);
          console.warn(o);
        },
        error: (o: unknown) => {
          logToSentry('error', o);
          console.error(o);
        },
        fatal: (o: unknown) => {
          logToSentry('fatal', o);
          console.error(o);
        },
      },
    },
    level: config.environment === 'development' ? 'debug' : 'info',
  });

  const logger: Logger = {
    trace: (message: string, ...args: unknown[]) => {
      pinoLogger.trace({ args }, message);
    },
    debug: (message: string, ...args: unknown[]) => {
      pinoLogger.debug({ args }, message);
    },
    info: (message: string, ...args: unknown[]) => {
      pinoLogger.info({ args }, message);
    },
    warn: (message: string, ...args: unknown[]) => {
      pinoLogger.warn({ args }, message);
    },
    error: (messageOrError: string | Error, ...args: unknown[]) => {
      if (messageOrError instanceof Error) {
        // For Error objects, capture in Sentry and log
        Sentry.captureException(messageOrError, {
          contexts: {
            extra: {
              args,
            },
          },
        });
        pinoLogger.error({ err: messageOrError, args }, messageOrError.message);
      } else {
        pinoLogger.error({ args }, messageOrError);
      }
    },
    fatal: (messageOrError: string | Error, ...args: unknown[]) => {
      if (messageOrError instanceof Error) {
        // For Error objects, capture in Sentry and log
        Sentry.captureException(messageOrError, {
          level: 'fatal',
          contexts: {
            extra: {
              args,
            },
          },
        });
        pinoLogger.fatal({ err: messageOrError, args }, messageOrError.message);
      } else {
        Sentry.captureMessage(messageOrError, {
          level: 'fatal',
          contexts: {
            extra: {
              args,
            },
          },
        });
        pinoLogger.fatal({ args }, messageOrError);
      }
    },
  };

  loggerInstance = logger;
  return logger;
}

function logToSentry(level: 'trace' | 'debug' | 'info' | 'warning' | 'error' | 'fatal', data: unknown): void {
  // Only send breadcrumbs for info and above to avoid noise
  // Similar to pinoIntegration's log.levels configuration
  if (level === 'trace' || level === 'debug') {
    return;
  }

  // Normalize Sentry level
  const sentryLevel: Sentry.SeverityLevel = level === 'fatal' ? 'fatal' : level === 'warning' ? 'warning' : level;

  if (typeof data === 'object' && data !== null) {
    const logObject = data as Record<string, unknown>;

    // Add breadcrumb with structured data (similar to pino's structured logging)
    Sentry.addBreadcrumb({
      category: 'pino.log', // Indicate this is from our pino logger
      message: (logObject['msg'] as string) || 'Log message',
      level: sentryLevel,
      data: {
        ...logObject,
        logger: 'pino', // Tag to identify pino logs in Sentry
      },
    });

    // For error and fatal levels, also capture as Sentry event
    // Similar to pinoIntegration's error.levels configuration
    if (level === 'error' || level === 'fatal') {
      const message = (logObject['msg'] as string) || 'Log error';
      Sentry.captureMessage(message, {
        level: sentryLevel,
        contexts: {
          pino: {
            ...logObject,
          },
        },
        tags: {
          logger: 'pino',
          logLevel: level,
        },
      });
    }
  } else {
    Sentry.addBreadcrumb({
      category: 'pino.log',
      message: String(data),
      level: sentryLevel,
      data: {
        logger: 'pino',
      },
    });
  }
}

/**
 * Get the logger instance
 * This should be called after createLogger has been invoked
 */
export function getLogger(): Logger {
  if (!loggerInstance) {
    throw new Error('Logger not initialized. Call createLogger() or initializeLogging() first.');
  }
  return loggerInstance;
}

/**
 * Get the logger instance safely, returns undefined if not initialized
 * Useful for optional logging in components
 */
export function getLoggerSafe(): Logger | undefined {
  return loggerInstance;
}
